import {
  AddCircleOutlineRounded as AddCircleOutlineRoundedIcon,
  DeleteRounded as DeleteRoundedIcon
} from '@mui/icons-material'
import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from '@mui/material'
import { t } from 'i18next'
import Joi from 'joi'
import { useState } from 'react'

import useFieldValidation from '../../../../helpers/fieldValidation'
import { useValidationEffect } from '../../../../helpers/hooks'
import Can from '../../../Layout/Can/Can'

const schema = Joi.object({
  name: Joi.string().empty(null).required(),
  url: Joi.string().empty(null).required(),
  hmacEnabled: Joi.boolean().optional(),
  hmacHeaderName: Joi.when('hmacEnabled', {
    is: true,
    then: Joi.string().empty(null).required(),
    otherwise: Joi.optional()
  }),
  hmacKey: Joi.when('hmacEnabled', {
    is: true,
    then: Joi.string().empty(null).required(),
    otherwise: Joi.optional()
  })
})
const ConfigureActionWebhook = ({
  webhookConfig,
  actionName,
  onChangeIsValid,
  onChangeWebhookConfig,
  onChangeActionName
}) => {
  const [name, setName] = useState(actionName)
  const [url, setUrl] = useState(null)
  const [sendTriggerInformation, setSendTriggerInformation] = useState(false)
  const [hmacEnabled, setHmacEnabled] = useState(false)
  const [hmacHeaderName, setHmacHeaderName] = useState('')
  const [hmacKey, setHmacKey] = useState('')
  const [validation, setValidation] = useState({ isValid: false })

  const handleNameChange = (event) => setName(event.target.value)

  const [headers, setHeaders] = useState([])

  const handleAddHeader = () => setHeaders([...headers, {
    key: '',
    value: ''
  }])

  const handleRemoveHeader = (indexToRemove) => {
    const newHeaders = headers.filter((_, index) => index !== indexToRemove)
    const confHeaders = newHeaders.reduce((prev, current) => {
      return {
        ...prev,
        [current.key]: current.value
      }
    }, {})

    onChangeWebhookConfig({
      ...webhookConfig,
      httpHeaders: confHeaders
    })
    setHeaders(newHeaders)
  }

  const handleHttpHeadersKey = (index, event) => {
    headers[index].key = event.target.value

    const confHeaders = headers.reduce((prev, current) => {
      return {
        ...prev,
        [current.key]: current.value
      }
    }, {})

    onChangeWebhookConfig({
      ...webhookConfig,
      httpHeaders: confHeaders
    })
    setHeaders(headers)
  }

  const handleHttpHeadersValue = (index, event) => {
    headers[index].value = event.target.value

    const confHeaders = headers.reduce((prev, current) => {
      return {
        ...prev,
        [current.key]: current.value
      }
    }, {})

    onChangeWebhookConfig({
      ...webhookConfig,
      httpHeaders: confHeaders
    })
    setHeaders(headers)
  }

  const handleChangeCheckbox = (_event, checked) =>
    setSendTriggerInformation(checked)

  const handleHmacEnabledChange = (_event, checked) => {
    setHmacEnabled(checked)
    if (!checked) {
      setHmacHeaderName('')
      setHmacKey('')
    }
  }

  const handleHmacHeaderNameChange = (event) => setHmacHeaderName(event.target.value)

  const handleHmacKeyChange = (event) => setHmacKey(event.target.value)

  const handleUrlAction = (event) => setUrl(event.target.value)

  useValidationEffect(() => {
    const fieldValidation = useFieldValidation({
      name: name,
      url,
      hmacEnabled,
      hmacHeaderName,
      hmacKey
    }, schema)
    setValidation(fieldValidation)
    onChangeIsValid(fieldValidation.isValid)
    onChangeActionName(name)
    onChangeWebhookConfig({
      ...webhookConfig,
      urlAction: url,
      sendTriggerInformation: sendTriggerInformation,
      payloadEncoding: {
        hmacEnabled,
        hmacHeaderName,
        hmacKey
      }
    })
  }, [name, url, sendTriggerInformation, hmacEnabled, hmacHeaderName, hmacKey])

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid
        container
        direction={'column'}
        marginBottom={5}
      >
        <Grid
          size={12}
          paddingY={1}
        >
          <TextField
            id="configure-action-name"
            variant="standard"
            label={t('actions.actionName')}
            fullWidth
            value={actionName}
            onChange={handleNameChange}
            error={validation.messages?.name?.length > 0}
            helperText={validation.messages?.name?.join(', ')}
          />
        </Grid>

        <Grid
          size={12}
          paddingY={1}
        >
          <TextField
            id="configure-action-webhook-url"
            variant="standard"
            label={t('actions.actionUrl')}
            value={webhookConfig.urlAction}
            onChange={handleUrlAction}
            fullWidth
            error={validation.messages?.url?.length > 0}
            helperText={validation.messages?.url?.join(', ')}
          />
        </Grid>

        <Grid
          size={12}
          paddingY={1}
          display="flex"
        >
          <Typography
            variant="h5"
            fontWeight="bold"
          >
            {t('actions.httpHeaders')}
          </Typography>
          <Button
            id="configure-action-webhook-http-headers"
            sx={{ height: '24px' }}
            onClick={handleAddHeader}
          >
            <AddCircleOutlineRoundedIcon />
          </Button>
        </Grid>

        <Grid>
          <FormControlLabel
            control={<Checkbox onChange={handleChangeCheckbox} />}
            label={t('actions.sendTriggerInformation')}
          />
        </Grid>

        <Grid>
          <FormControlLabel
            control={<Checkbox checked={hmacEnabled} onChange={handleHmacEnabledChange} />}
            label="Codifica HMAC"
          />
        </Grid>

        {hmacEnabled && (
          <>
            <Grid
              size={12}
              paddingY={1}
            >
              <TextField
                id="configure-action-webhook-hmac-header-name"
                variant="standard"
                label="Nome header http chiave"
                value={hmacHeaderName}
                onChange={handleHmacHeaderNameChange}
                fullWidth
                error={validation.messages?.hmacHeaderName?.length > 0}
                helperText={validation.messages?.hmacHeaderName?.join(', ')}
              />
            </Grid>

            <Grid
              size={12}
              paddingY={1}
            >
              <TextField
                id="configure-action-webhook-hmac-key"
                variant="standard"
                label="Chiave HMAC"
                value={hmacKey}
                onChange={handleHmacKeyChange}
                fullWidth
                error={validation.messages?.hmacKey?.length > 0}
                helperText={validation.messages?.hmacKey?.join(', ')}
              />
            </Grid>
          </>
        )}

        <Grid>
          <Stack
            direction={'column'}
            spacing={2}
          >
            {headers.map((_, index) => (
              <Stack
                direction={'row'}
                key={index}
                spacing={2}
              >
                <TextField
                  name="configure-action-webhook-http-header-key"
                  variant="standard"
                  label={t('common.key')}
                  onChange={(event) => handleHttpHeadersKey(index, event)}
                  size="small"
                ></TextField>
                <TextField
                  name="configure-action-webhook-http-header-value"
                  variant="standard"
                  label={t('common.value')}
                  onChange={(event) => handleHttpHeadersValue(index, event)}
                  size="small"
                ></TextField>
                <Can action={'automations_d'}>
                  <Button
                    variant="text"
                    key="trash"
                    onClick={() => handleRemoveHeader(index)}
                  >
                    <DeleteRoundedIcon
                      size={16}
                      color="red"
                    />
                  </Button>
                </Can>
              </Stack>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ConfigureActionWebhook
